import "./MetaIntegration.css";
import React, { useEffect, useState } from "react";
import { Card, Button, Table, Tag, Space, Popconfirm, message, Alert } from "antd";
import {
  getMetaConnectUrlApi,
  fetchMetaIntegrationsApi,
  disconnectMetaIntegrationApi,
  fetchConnectedMetaFormsApi,
} from "../../../../apiservice/crm";
import { convertToCompanyTimezoneObject } from "../../../../lib/functions";
import MetaFormsModal from "./MetaFormsModal";
import MetaFormConfigModal from "./MetaFormConfigModal";

const MetaIntegration = ({ refreshToken, users }) => {
  const [loading, setLoading] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [formsModalOpen, setFormsModalOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [connectedForms, setConnectedForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);

  const openFormsModal = (row) => {
    setSelectedIntegration(row);
    setFormsModalOpen(true);
  };

  const loadIntegrations = async () => {
    try {
      setLoading(true);
      const res = await fetchMetaIntegrationsApi();
      setRows(res.data || []);
    } catch (err) {
      message.error("Failed to load Meta integrations");
    } finally {
      setLoading(false);
    }
  };

  const loadConnectedForms = async () => {
  try {
    setFormsLoading(true);
    const res = await fetchConnectedMetaFormsApi();
    setConnectedForms(res?.data?.data || []);
  } catch (err) {
    message.error("Failed to load connected Meta forms");
  } finally {
    setFormsLoading(false);
  }
};

  useEffect(() => {
    loadIntegrations();
    loadConnectedForms();
  }, [refreshToken]);

  

  const handleConnect = async () => {
    try {
      setConnectLoading(true);
      const res = await getMetaConnectUrlApi();
      const url = res?.data?.url;
      if (!url) {
        return message.error("Meta connect URL not found");
      }
      window.location.href = url;
    } catch (err) {
      message.error("Failed to connect Meta");
    } finally {
      setConnectLoading(false);
    }
  };

  const handleDisconnect = async (integration_id) => {
    try {
      await disconnectMetaIntegrationApi({ integration_id });
      message.success("Meta integration disconnected");
      loadIntegrations();
      loadConnectedForms();
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to disconnect Meta integration"
      );
    }
  };

  const formCountByPage = connectedForms.reduce((acc, row) => {
    acc[row.page_id] = (acc[row.page_id] || 0) + 1;
    return acc;
  }, {});

  const columns = [
    {
      title: "Page Name",
      dataIndex: "page_name",
      key: "page_name",
    },
    {
      title: "Page ID",
      dataIndex: "page_id",
      key: "page_id",
    },
    {
      title: "Instagram ID",
      dataIndex: "instagram_id",
      key: "instagram_id",
      render: (v) => v || "-",
    },
    
    {
      title: "Connected Forms",
      key: "connected_forms_count",
      render: (_, row) => formCountByPage[row.page_id] || 0,
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => (
        <Tag color={v === "active" ? "green" : "default"}>
          {v}
        </Tag>
      ),
    },
    {
      title: "Connected On",
      dataIndex: "created_at",
      key: "created_at",
      render: (v) => (v ? convertToCompanyTimezoneObject(v).format("DD-MM-YYYY HH:mm") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => (
        <Space>
          {row.status === "active" && (
            <>
              <Button
                size="small"
                onClick={() => openFormsModal(row)}
              >
                Manage Forms
              </Button>

              <Popconfirm
                title="Disconnect this Meta page?"
                onConfirm={() => handleDisconnect(row.id)}
              >
                <Button danger size="small">Disconnect</Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];


  const formColumns = [
    {
      title: "Page Name",
      dataIndex: "page_name",
      key: "page_name",
    },
    {
      title: "Form Name",
      dataIndex: "form_name",
      key: "form_name",
    },
    {
      title: "Form ID",
      dataIndex: "form_id",
      key: "form_id",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => (
        <Tag color={v === "active" ? "green" : "default"}>
          {v}
        </Tag>
      ),
    },
    {
      title: "Configured On",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (v) => (v ? convertToCompanyTimezoneObject(v).format("DD-MM-YYYY HH:mm") : "-"),
    },
    {
        title: "Actions",
        key: "actions",
        render: (_, row) => (
            <Button
                type="link"
                onClick={() => {
                    setSelectedForm(row);
                    setConfigOpen(true);
                }}
            >
                {row.configured ? "Reconfigure" : "Configure"}
            </Button>

        )
    }
  ];

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr)",
        gap: 16,
        maxWidth: "100%",
      }}
    >
      <Card
        className="meta-integration-card"
        title="Meta Lead Ads Integration"
        styles={{
          header: {
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            rowGap: 8,
          },
        }}
        extra={
          <Button
            type="primary"
            loading={connectLoading}
            onClick={handleConnect}
          >
            Connect Meta
          </Button>
        }
      >
        <div style={{ color: "#555", lineHeight: 1.8 }}>
          Connect your Facebook / Instagram lead forms to automatically import
          leads into CRM. New lead submissions will be created as CRM leads and
          logged in the lead activity timeline.
        </div>
      </Card>

      <Alert
        type="info"
        showIcon
        message="Setup note"
        description="After connecting Meta, open Manage Forms for a connected page and select the lead forms you want to import into CRM. Only selected forms properly configured will create CRM leads."
      />

      <Card title={`Connected Pages (${rows.length})`}>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows}
          columns={columns}
          pagination={false}
          scroll={{ x: "max-content" }}
           locale={{
            emptyText: "No Meta pages connected yet. Click Connect Meta to authorize a Facebook account and sync pages.And for forms"
          }}
        />
      </Card>

      <Card title={`Connected Lead Forms (${connectedForms.length})`}>
        <Table
          rowKey="id"
          loading={formsLoading}
          dataSource={connectedForms}
          columns={formColumns}
          pagination={false}
          scroll={{ x: "max-content" }}
          locale={{ emptyText: "No Meta forms connected yet. Use Manage Forms on a connected page to choose which lead forms should import into CRM." }}
        />
      </Card>

      <MetaFormsModal
        open={formsModalOpen}
        integration={selectedIntegration}
        onClose={() => {
          setFormsModalOpen(false);
          setSelectedIntegration(null);
        }}
        onSaved={() => {
          loadConnectedForms();
        }}
      />

      <MetaFormConfigModal
          open={configOpen}
          form={selectedForm}
          users={users}
          onClose={() => {
              setConfigOpen(false);
              setSelectedForm(null);
          }}
          onSaved={() => {
              loadConnectedForms();
          }}
      />
    </div>
  );
};

export default MetaIntegration;